import { ChangeEvent, FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SmoMoonType } from '../../../../Models/Smo/SmoMoonType';
import DebugActions from '../../../../Modules/Redux/Global/Debug/DebugActions';
import DebugSelectors from '../../../../Modules/Redux/Global/Debug/DebugSelectors';
import { Optional } from '../../../../Utils/Types';
import { Box } from '../Box/Box';
import { BoxSection } from '../BoxSection/BoxSection';
import { Button } from '../Button/Button';
import { DisclosureGroup } from '../DisclosureGroup/DisclosureGroup';
import { NavigationBar } from '../NavigationBar/NavigationBar';
import { PageLayout } from '../PageLayout/PageLayout';
import { ScrollView } from '../ScrollView/ScrollView';
import { DebugMenuProps } from './DebugMenuProps';

export const DebugMenu: FunctionComponent<DebugMenuProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const isImageProcessingPaused: boolean = useSelector(DebugSelectors.isImageProcessingPaused);
  const singleMoonFilterBrightness: Optional<number> = useSelector(DebugSelectors.singleMoonFilterBrightness);
  const multiMoonFilterBrightness: Optional<number> = useSelector(DebugSelectors.multiMoonFilterBrightness);
  const singleMoonComparisonMisMatchPercent: Optional<number> = useSelector(
    DebugSelectors.singleMoonComparisonMisMatchPercent
  );
  const singleMoonCroppedTitles: string[] = useSelector(DebugSelectors.singleMoonCroppedTitles);
  const singleMoonCroppedFilteredTitles: string[] = useSelector(DebugSelectors.singleMoonCroppedFilteredTitles);
  const singleMoonCroppedNames: string[] = useSelector(DebugSelectors.singleMoonCroppedNames);
  const singleMoonCroppedFilteredNames: string[] = useSelector(DebugSelectors.singleMoonCroppedFilteredNames);
  const multiMoonCroppedNames: string[] = useSelector(DebugSelectors.multiMoonCroppedNames);
  const multiMoonCroppedFilteredNames: string[] = useSelector(DebugSelectors.multiMoonCroppedFilteredNames);

  function didChangeSingleMoonFilter(e: ChangeEvent<HTMLInputElement>): void {
    const brightness = parseFloat(e.target.value);
    console.log(brightness);
    if (!isNaN(brightness)) {
      dispatch(DebugActions.updateFilterBrightness({ brightness, moonType: SmoMoonType.single }));
    }
  }

  function didChangeMultiMoonFilter(e: ChangeEvent<HTMLInputElement>): void {
    const brightness = parseFloat(e.target.value);
    if (!isNaN(brightness)) {
      dispatch(DebugActions.updateFilterBrightness({ brightness, moonType: SmoMoonType.multi }));
    }
  }

  function toggleImageProcessingPause(): void {
    dispatch(DebugActions.setIsImageProcessingPaused(!isImageProcessingPaused));
  }

  return (
    <div
      className="box-border fixed rounded-lg shadow-2xl left-8 bottom-8 bg-bg"
      style={{ width: '500px', height: '600px' }}
    >
      <ScrollView className="h-full pb-8">
        <NavigationBar title="Debug">
          <Button title={isImageProcessingPaused ? 'Resume' : 'Pause'} onClick={toggleImageProcessingPause} />
          <Button title="Close" onClick={onClose} />
        </NavigationBar>

        <PageLayout>
          <div className="space-y-2">
            <Box>
              <DisclosureGroup title="Filter values">
                <div className="space-y-2">
                  <BoxSection title="Single moon">
                    <input
                      type="number"
                      defaultValue={`${singleMoonFilterBrightness}`}
                      onChange={didChangeSingleMoonFilter}
                    />
                  </BoxSection>
                  <BoxSection title="Multi moon">
                    <input
                      type="number"
                      defaultValue={`${multiMoonFilterBrightness}`}
                      onChange={didChangeMultiMoonFilter}
                    />
                  </BoxSection>
                  <BoxSection title="Single moon comparison mismatch percent">
                    <h5>{singleMoonComparisonMisMatchPercent}</h5>
                  </BoxSection>
                </div>
              </DisclosureGroup>
            </Box>

            <Box>
              <DisclosureGroup title="Single moon title">
                <div className="space-y-1 overflow-y-scroll v-stack" style={{ maxHeight: '400px' }}>
                  {singleMoonCroppedTitles.map((url) => (
                    <img className="border border-gray-400" key={url} src={url} />
                  ))}
                </div>
              </DisclosureGroup>
            </Box>

            <Box>
              <DisclosureGroup title="Single moon filtered title">
                <div className="space-y-1 overflow-y-scroll v-stack" style={{ maxHeight: '400px' }}>
                  {singleMoonCroppedFilteredTitles.map((url) => (
                    <img className="border border-gray-400" key={url} src={url} />
                  ))}
                </div>
              </DisclosureGroup>
            </Box>

            <Box>
              <DisclosureGroup title="Single moon name">
                <div className="space-y-1 overflow-y-scroll v-stack" style={{ maxHeight: '400px' }}>
                  {singleMoonCroppedNames.map((url) => (
                    <img className="border border-gray-400" key={url} src={url} />
                  ))}
                </div>
              </DisclosureGroup>
            </Box>

            <Box>
              <DisclosureGroup title="Single moon filtered name">
                <div className="space-y-1 overflow-y-scroll v-stack" style={{ maxHeight: '400px' }}>
                  {singleMoonCroppedFilteredNames.map((url) => (
                    <img className="border border-gray-400" key={url} src={url} />
                  ))}
                </div>
              </DisclosureGroup>
            </Box>

            <Box>
              <DisclosureGroup title="Multi moon name">
                <div className="space-y-1 overflow-y-scroll v-stack" style={{ maxHeight: '400px' }}>
                  {multiMoonCroppedNames.map((url) => (
                    <img className="border border-gray-400" key={url} src={url} />
                  ))}
                </div>
              </DisclosureGroup>
            </Box>

            <Box>
              <DisclosureGroup title="Multi moon filtered name">
                <div className="space-y-1 overflow-y-scroll v-stack" style={{ maxHeight: '400px' }}>
                  {multiMoonCroppedFilteredNames.map((url) => (
                    <img className="border border-gray-400" key={url} src={url} />
                  ))}
                </div>
              </DisclosureGroup>
            </Box>
          </div>
        </PageLayout>
      </ScrollView>
    </div>
  );
};
